import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TickerWithName from "../components/TickerWithName";
import api from "../utils/api";
import { QuickTradeEquityButton } from "../components/QuickTradeButtons";



// -------- shared UI helpers (local to this file) --------
const money2 = (v) => {
    if (v === null || v === undefined || v === "") return "0.00";
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : String(v);
};
const pctChange = (entry, exit) => {
    const a = Number(entry), b = Number(exit);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
    return ((b - a) / a) * 100;
};
const EstExitLine = ({ entry, exit }) => {
    const pct = pctChange(entry, exit);
    const good = pct !== null && pct >= 0;
    return (
        <div className="text-sm">
            Est Exit: ${money2(exit)}{" "}
            {pct !== null && (
                <span className={`ml-1 px-2 py-0.5 rounded text-xs font-semibold ${good ? "bg-green-600" : "bg-red-600"}`}>
                    {good ? "+" : ""}{pct.toFixed(2)}%
                </span>
            )}
        </div>
    );
};

export default function AllNysePicks() {
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [limit, setLimit] = useState(50);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState("score");
    const [order, setOrder] = useState("desc");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setMsg("");
            try {
                const { data } = await api.get(`/api/all-nyse-picks`, {
                    timeout: 25000,
                    params: { page, limit, sort, order },
                });
                if (cancelled) return;
                setResults(data.results || []);
                setPages(data.pages || 1);
                setTotal(data.total || 0);
                if (data.error) setMsg(`Server error: ${data.error}`);
                else if (data.building) setMsg("Building… please check back in a moment.");
            } catch (e) {
                if (!cancelled) setMsg("Network error. Please try again.");
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [page, limit, sort, order]);

    const canPrev = page > 1;
    const canNext = page < pages;

    return (
        <div className="p-4 sm:p-5">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-blue-400">🗂️ All NYSE Picks</h2>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <label className="text-sm">
                    Show
                    <select
                        className="ml-2 bg-gray-800 border border-gray-700 rounded px-2 py-1"
                        value={limit}
                        onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value, 10)); }}
                    >
                        {[25, 50, 100, 150, 200].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    / page
                </label>

                <label className="text-sm">
                    Sort by
                    <select
                        className="ml-2 bg-gray-800 border border-gray-700 rounded px-2 py-1"
                        value={sort}
                        onChange={(e) => { setPage(1); setSort(e.target.value); }}
                    >
                        <option value="score">Score</option>
                        <option value="ticker">Ticker</option>
                    </select>
                </label>

                <label className="text-sm">
                    Order
                    <select
                        className="ml-2 bg-gray-800 border border-gray-700 rounded px-2 py-1"
                        value={order}
                        onChange={(e) => { setPage(1); setOrder(e.target.value); }}
                    >
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                    </select>
                </label>

                <div className="text-sm text-gray-400 ml-auto">
                    Total: {total.toLocaleString()} | Page {page} of {pages}
                </div>
            </div>

            {/* List / Messages */}
            {loading && <div className="text-gray-400">Loading…</div>}
            {msg && !loading && <div className="text-gray-300">{msg}</div>}

            <div className="space-y-3">
                {results.map((r, i) => (
                    <div
                        key={`${r.ticker}-${i}`}
                        className="bg-gray-700 p-3 sm:p-4 rounded-lg shadow hover:bg-gray-600 cursor-pointer"
                        onClick={() => navigate(`/chart/${r.ticker}`)}
                    >
                        <div className="text-lg sm:text-xl font-semibold">
                            <TickerWithName symbol={r.ticker} />
                        </div>
                        <div className="text-sm text-gray-300">Score: {r.score}</div>
                        <div className="text-sm">Entry: ${money2(r.entryPrice)}</div>
                        <EstExitLine entry={r.entryPrice} exit={r.estimatedExit} />
                        <div className="text-sm text-gray-400">{r.reason}</div>
                        <QuickTradeEquityButton ticker={r.ticker} />
                    </div>
                ))}

                {!loading && !msg && results.length === 0 && (
                    <div className="text-gray-400">No results.</div>
                )}
            </div>

            {/* Pagination */}
            <div className="flex items-center gap-2 mt-6">
                <button
                    disabled={!canPrev}
                    onClick={() => setPage(1)}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canPrev ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"}`}
                >
                    « First
                </button>
                <button
                    disabled={!canPrev}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canPrev ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"}`}
                >
                    ‹ Prev
                </button>
                <button
                    disabled={!canNext}
                    onClick={() => setPage(p => Math.min(pages, p + 1))}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canNext ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"}`}
                >
                    Next ›
                </button>
                <button
                    disabled={!canNext}
                    onClick={() => setPage(pages)}
                    className={`px-3 py-1 rounded bg-gray-800 border border-gray-700 ${!canNext ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-700"}`}
                >
                    Last »
                </button>
            </div>
        </div>
    );
}
